// A sample fantasy week, on real data. `/lab/week`
//
// A live-like test page on real data, sampling a week of managing a team —
// setting a lineup, then the week's results. It is an admin-style page that
// connects to nothing else.
//
// A LAB SURFACE, like `/lab/player/:id` and by the same rules: not in the nav,
// not in DESIGN_PLAN, not a designed screen, and it must either graduate into
// a real design or be deleted rather than drift (HANDOFF, "Development
// surfaces, not screens"). The three things it is careful about are the three
// that would make it misleading rather than merely rough:
//
//   NOTHING INVENTED. Every point on this page is the real scoring engine run
//   over a real nflverse stat line. A starter with no line is drawn as "did not
//   play" and contributes nothing, because a dash and a 0.0 mean different
//   things and only one of them is true here.
//
//   NOTHING ASSERTED. No sentence tells anybody who to play
//   (`tools-not-instructions`). The lineup is filled by a rule, and the rule is
//   printed on the page rather than implied by the result.
//
//   EVERYTHING TRACEABLE. The footer names the week, the source, how many lines
//   fed it, which earlier weeks fed the ranking, and when the data was last
//   confirmed.
//
// WHY THERE IS NO LEAGUE BEHIND IT. There is no league. The two squads are
// derived per request and stored nowhere, so nothing here can be "managed" in
// the sense the real screens will mean — which is the honest limit of a sample
// and is said on the page rather than left to be discovered.

import { useSearchParams } from 'react-router-dom';
import { LabWeekView, type LabPlayer, type LabTeam } from '@fsp/domain';
import { Cap, Surface } from '../kit';
import { Panel } from '../kit/Panel';
import { useRead } from '../data/read';
import '../styles/screens/lab.css';

/** Points, or the dash that means we have no line rather than a zero. */
function Points({ value, dim = false }: { value: number | null; dim?: boolean }) {
  return (
    <span className={`fsp-num fsp-lab-points${value === null ? ' none' : dim ? ' dim' : ''}`}>
      {value === null ? '—' : value.toFixed(1)}
    </span>
  );
}

function PlayerLine({
  player,
  slot,
  showActual,
}: {
  player: LabPlayer | null;
  slot?: string;
  showActual: boolean;
}) {
  return (
    // Three number columns and a name at 390. The numbers are fixed and the
    // name takes the rest, ellipsized — measured at phone width.
    <div className={`fsp-lab-line${slot === undefined ? ' bench' : ''}`}>
      {slot !== undefined && <Cap>{slot}</Cap>}
      <div className="fsp-grow">
        {player === null ? (
          // A slot nobody could fill. It happens when a position has no real
          // lines for the week, and saying so beats drawing an empty row.
          <span className="fsp-lab-none">nobody eligible</span>
        ) : (
          <>
            <div className="fsp-ui-nowrap">{player.name}</div>
            <div className="fsp-lab-sub">
              {player.positions.join(' · ')}
              {player.proTeam !== null && ` · ${player.proTeam}`}
              {showActual && player.actual === null && ' · no line this week'}
            </div>
          </>
        )}
      </div>
      <div className="fsp-right">
        <Points value={player?.form ?? null} dim />
      </div>
      <div className="fsp-right">
        <Points value={player?.projected ?? null} dim />
      </div>
      {showActual && (
        <div className="fsp-right">
          <Points value={player?.actual ?? null} />
        </div>
      )}
    </div>
  );
}

function Heads({ showActual }: { showActual: boolean }) {
  return (
    <div className="fsp-lab-heads">
      <span />
      <span />
      <div className="fsp-right">
        <Cap>Form</Cap>
      </div>
      <div className="fsp-right">
        <Cap>Proj</Cap>
      </div>
      {showActual && (
        <div className="fsp-right">
          <Cap>Week</Cap>
        </div>
      )}
    </div>
  );
}

function TeamCard({ team, showActual }: { team: LabTeam; showActual: boolean }) {
  return (
    <div>
      <div className="fsp-lab-team-head">
        {/* The team's NAME is the panel's title, so repeating it here would
            print it twice in a row — caught in the 390 walk. What the header
            carries instead is what the number means, which the two big totals
            need and the panel title cannot say. */}
        <Cap>{showActual ? 'Projected → actual' : 'Projected'}</Cap>
        <span className="fsp-num fsp-lab-totals">
          <span className="fsp-lab-projected">
            <Points value={team.projectedTotal} dim />
          </span>
          {showActual && (
            <>
              <span className="fsp-lab-arrow">→</span>
              <Points value={team.total} />
            </>
          )}
        </span>
      </div>
      <Heads showActual={showActual} />
      {team.starters.map((s, i) => (
        <PlayerLine
          key={`${s.slot}-${i}`}
          slot={s.slot}
          player={s.player}
          showActual={showActual}
        />
      ))}
      <div className="fsp-lab-bench">
        <Cap>Bench</Cap>
        {team.bench.length === 0 ? (
          <div className="fsp-lab-none fsp-lab-nobody">nobody</div>
        ) : (
          team.bench.map((p) => <PlayerLine key={p.id} player={p} showActual={showActual} />)
        )}
      </div>
    </div>
  );
}

const STATE_NOTE: Record<string, string> = {
  final: 'Every game of this week has finished.',
  partial: 'Some games of this week have not finished, so these totals will still move.',
  none: 'No stat lines have been published for this week yet.',
};

export function LabWeekScreen() {
  const [search] = useSearchParams();
  const season = search.get('season');
  const week = search.get('week');
  const query = new URLSearchParams();
  if (season !== null) query.set('season', season);
  if (week !== null) query.set('week', week);
  const suffix = query.toString();

  const { snapshot } = useRead(`/lab/week${suffix === '' ? '' : `?${suffix}`}`, LabWeekView);

  if (snapshot.state === 'loading') {
    return (
      <div className="fsp-screen fsp-lab-padded">
        <Surface>
          <Cap>Loading</Cap>
        </Surface>
      </div>
    );
  }

  const view = snapshot.state === 'ready' ? snapshot.value : snapshot.lastValue;
  if (view === null) {
    // Fail loudly, in the server's own words. An empty page here would read as
    // "this week had no football in it", which is never what happened.
    return (
      <div className="fsp-screen fsp-lab-padded">
        <Surface>
          <h1 className="fsp-disp fsp-lab-title">No sample week</h1>
          <p className="fsp-lab-secondary">
            {snapshot.state === 'failed' ? snapshot.failure.message : 'Nothing was asked for.'}
          </p>
          <Cap>{snapshot.state === 'failed' ? snapshot.failure.code : 'none'}</Cap>
        </Surface>
      </div>
    );
  }

  const showActual = view.state !== 'none';
  const home = view.home;
  const away = view.away;
  const decided = showActual && home.total !== null && away.total !== null;

  return (
    // THIS PAGE OWNS ITS OWN SCROLLING, because it lives OUTSIDE `AppShell`.
    //
    // The bench and the provenance panel were cut off at the bottom of a
    // phone with no way to reach them, and the first fix was wrong: it kept
    // `.fsp-screen` and put `overflow: auto` on the child. `.fsp-screen` is
    // `flex: 1; min-height: 0`, which only constrains anything inside a flex
    // column that already has a height — and the shell is what provides that.
    // A public route hangs off `#root`, which is `display: block` with a fixed
    // height and `overflow: hidden`, so `flex: 1` did nothing, the element grew
    // to 2783px inside an 844px box, and the overflow was clipped by a parent
    // that does not scroll. Measured, not guessed.
    //
    // So: full height, and one scrolling region inside it. CLAUDE.md, "the
    // browser never scrolls; regions do" — this is the region.
    <div className="fsp-lab-scroll">
      <div className="fsp-lab-page">
        <div>
          <h1 className="fsp-disp fsp-lab-title">
            Sample week {view.week}, {view.season}
          </h1>
          <p className="fsp-lab-secondary fsp-lab-lede">
            Real {view.source} data through the real scoring engine. Nothing here is a league — the
            two squads are built from scratch every time this page loads, and no row is stored.
          </p>
        </div>

        <Panel title="The week">
          <div className="fsp-lab-stack">
            <div className="fsp-lab-state">
              <Cap>{view.state}</Cap>
              <span className="fsp-lab-secondary">{STATE_NOTE[view.state]}</span>
            </div>
            {decided && (
              <div className="fsp-lab-lead">
                <strong>{home.total! > away.total! ? home.name : away.name}</strong>{' '}
                <span className="fsp-lab-secondary">
                  leads {Math.max(home.total!, away.total!).toFixed(1)} to{' '}
                  {Math.min(home.total!, away.total!).toFixed(1)}
                </span>
              </div>
            )}
          </div>
        </Panel>

        {/* One column on a phone, two once there is room for them side by
            side. A matchup read as two stacked lists on a wide screen is the
            one thing this page exists to show is wrong. */}
        <div className="fsp-lab-pair">
          <Panel title="Your team">
            <TeamCard team={home} showActual={showActual} />
          </Panel>
          <Panel title="The opponent">
            <TeamCard team={away} showActual={showActual} />
          </Panel>
        </div>

        <Panel title="Where this came from">
          <div className="fsp-lab-source">
            <div>
              <strong>Roster.</strong> {view.rules.roster}
            </div>
            <div>
              <strong>Lineup.</strong> {view.rules.lineup}
            </div>
            <div>
              <strong>Form</strong> is mean points per earlier week the player actually played,
              under the {view.scoring} preset. It is arithmetic on the past, not a forecast, and not
              the house projection, which is a separate number.
            </div>
            <div>
              <strong>Window</strong> {view.windowId} · {view.lineCounts.week} lines this week ·{' '}
              {view.lineCounts.basis} lines across{' '}
              {view.basis.length === 1 ? '1 earlier week' : `${view.basis.length} earlier weeks`}
              {view.basis.length > 0 && ` (${view.basis.join(', ')})`}
            </div>
            <div>
              <strong>Last confirmed</strong>{' '}
              {view.asOf === null ? 'unknown' : new Date(view.asOf).toLocaleString()}
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
}
